
import React, { useState, useMemo, useEffect } from 'react';
import { Transaction, CreditCard, CategoryType, UserProfile } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, getTrustedDate } from '../utils/helpers';
import { CATEGORY_CONFIG } from '../constants';
import TransactionModal from './TransactionModal';

interface DashboardProps {
  transactions: Transaction[];
  cards: CreditCard[];
  onAddTransaction: (t: Transaction | Transaction[]) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  needsSetup: boolean;
  onTriggerSetup: () => void;
  user: UserProfile | null;
  userStatus: string;
}

const CustomTooltip = ({ active, payload, label, showValues }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 outline-none animate-in zoom-in-95 duration-200">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.fill }}></div>
                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">{entry.name}:</span>
              </div>
              <span className={`text-[10px] font-black ${
                entry.name === 'Entradas' ? 'text-emerald-600' : 
                entry.name === 'Cartão' ? 'text-amber-500' : 
                'text-red-600'
              }`}>
                {showValues ? formatCurrency(entry.value) : "R$ ••••"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const Dashboard: React.FC<DashboardProps> = ({ transactions, cards, onAddTransaction, theme, toggleTheme, needsSetup, onTriggerSetup, user, userStatus }) => {
  const now = getTrustedDate();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [showValues, setShowValues] = useState(() => {
    const saved = localStorage.getItem('equilibra_privacy_mode');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const handlePrivacyChange = () => {
      const saved = localStorage.getItem('equilibra_privacy_mode');
      setShowValues(saved === null ? true : saved === 'true');
    };
    window.addEventListener('privacyModeChanged', handlePrivacyChange);
    return () => window.removeEventListener('privacyModeChanged', handlePrivacyChange);
  }, []);

  const togglePrivacy = () => {
    const newVal = !showValues;
    setShowValues(newVal);
    localStorage.setItem('equilibra_privacy_mode', newVal.toString());
    window.dispatchEvent(new Event('privacyModeChanged'));
  };

  const handleAddClick = () => {
    if (needsSetup) {
      onTriggerSetup();
    } else {
      setIsModalOpen(true);
    }
  };

  const fullMonths = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set<number>([currentYear]);
    transactions.forEach(t => {
      if (t.date) {
        const y = parseInt(t.date.split('-')[0]);
        if (y) yearsSet.add(y);
      }
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [transactions]);

  const stats = useMemo(() => {
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    const safeCards = Array.isArray(cards) ? cards : [];
    
    const currentTransactions = safeTransactions.filter(t => {
      if (!t.date) return false;
      const d = new Date(t.date + 'T12:00:00');
      let m = d.getMonth();
      let y = d.getFullYear();

      if (t.cardId && t.cardId !== '') {
        const card = safeCards.find(c => c.id === t.cardId);
        const closing = card?.closingDate || 5;
        if (d.getDate() > closing) {
          m++;
          if (m > 11) { m = 0; y++; }
        }
      }

      return y === selectedYear && m === selectedMonth;
    });

    const monthIncome = currentTransactions
      .filter(t => t.type === 'income' && (!t.cardId || t.cardId === ''))
      .reduce((acc, t) => acc + (t.amount || 0), 0);

    const monthCashExpenses = currentTransactions
      .filter(t => (t.type === 'expense' && (!t.cardId || t.cardId === '')) || t.category === 'Pagamento de Fatura')
      .reduce((acc, t) => acc + (t.amount || 0), 0);

    const currentMonthBalance = monthIncome - monthCashExpenses;

    const monthCardDebt = currentTransactions.reduce((acc, t) => {
      if (t.cardId && t.cardId !== '') {
        if (t.category === 'Pagamento de Fatura') return acc - (t.amount || 0);
        return t.type === 'expense' ? acc + (t.amount || 0) : acc - (t.amount || 0);
      }
      return acc;
    }, 0);

    const totalMonthExpense = currentTransactions
      .filter(t => t.type === 'expense' && t.category !== 'Pagamento de Fatura')
      .reduce((acc, t) => acc + (t.amount || 0), 0);

    const solvencyMargin = currentMonthBalance - monthCardDebt;
    
    let riskAlert = null;

    if (currentTransactions.length > 0) {
      if (solvencyMargin < 0) {
        riskAlert = {
          type: 'danger',
          title: 'Orçamento Estourado',
          message: `Seus gastos totais (dinheiro + cartão) superaram sua entrada em ${formatCurrency(Math.abs(solvencyMargin))}.`,
          icon: 'fa-triangle-exclamation'
        };
      }
      else if (monthCardDebt > 0 && currentMonthBalance > 0 && monthCardDebt >= currentMonthBalance * 0.9) {
        riskAlert = {
          type: 'danger',
          title: 'Saldo Baixo e Fatura em Aberto',
          message: `Atenção: Sua fatura de ${formatCurrency(monthCardDebt)} está consumindo quase todo seu saldo disponível. Restam apenas ${formatCurrency(solvencyMargin)} de margem real.`,
          icon: 'fa-credit-card'
        };
      }
      else if (currentMonthBalance <= 100 && currentMonthBalance > 0) {
        riskAlert = {
          type: 'warning',
          title: 'Reserva em Alerta',
          message: `Seu saldo líquido em mãos está em ${formatCurrency(currentMonthBalance)}. Evite novas compras no débito ou dinheiro.`,
          icon: 'fa-piggy-bank'
        };
      }
    }

    const totalGlobalDebt = safeTransactions.reduce((acc, t) => {
      if (t.cardId && t.cardId !== '') {
        if (t.category === 'Pagamento de Fatura') return acc - (t.amount || 0);
        return t.type === 'expense' ? acc + (t.amount || 0) : acc - (t.amount || 0);
      }
      return acc;
    }, 0);

    const totalCardLimit = safeCards.reduce((acc, c) => acc + (c.limit || 0), 0);
    const availableCredit = totalCardLimit - totalGlobalDebt;
    const creditUsagePercent = totalCardLimit > 0 ? (Math.max(0, totalGlobalDebt) / totalCardLimit) * 100 : 0;

    const categorySummary = currentTransactions.reduce((acc, t) => {
      if (t.type === 'expense') {
        acc[t.category] = (acc[t.category] || 0) + (t.amount || 0);
      }
      return acc;
    }, {} as Record<string, number>);

    return { 
      income: monthIncome, 
      expense: totalMonthExpense,
      balance: currentMonthBalance,
      totalCardDebt: Math.max(0, monthCardDebt),
      availableCredit,
      creditUsagePercent,
      riskAlert,
      categorySummary: Object.entries(categorySummary).sort((a, b) => (b[1] as any) - (a[1] as any)).slice(0, 4),
      subscriptionWarning: (() => {
        if (!user?.subscription_expiry || userStatus === 'expired') return null;
        const expiry = new Date(user.subscription_expiry);
        const now = new Date();
        const diff = expiry.getTime() - now.getTime();
        const oneDay = 24 * 60 * 60 * 1000;
        const threeDays = 3 * oneDay;

        if (diff > 0 && diff <= threeDays) {
          const days = Math.ceil(diff / oneDay);
          return {
            days,
            message: days === 1 ? 'Sua assinatura expira amanhã!' : `Sua assinatura expira em ${days} dias.`,
            type: days === 1 ? 'danger' : 'warning'
          };
        }
        return null;
      })()
    };
  }, [transactions, cards, selectedYear, selectedMonth, user, userStatus]);

  const chartData = useMemo(() => {
    return fullMonths.map((name, index) => {
      const monthTrans = transactions.filter(t => {
        if (!t.date) return false;
        const d = new Date(t.date + 'T12:00:00');
        let m = d.getMonth();
        let y = d.getFullYear();

        if (t.cardId && t.cardId !== '') {
          const card = cards.find(c => c.id === t.cardId);
          const closing = card?.closingDate || 5;
          if (d.getDate() > closing) {
            m++;
            if (m > 11) { m = 0; y++; }
          }
        }

        return y === selectedYear && m === index;
      });
      const income = monthTrans.filter(t => t.type === 'income' && (!t.cardId || t.cardId === '')).reduce((acc, t) => acc + (t.amount || 0), 0);
      const cashExpense = monthTrans.filter(t => t.type === 'expense' && (!t.cardId || t.cardId === '') && t.category !== 'Pagamento de Fatura').reduce((acc, t) => acc + (t.amount || 0), 0);
      const cardExpense = monthTrans.filter(t => t.type === 'expense' && t.cardId && t.cardId !== '' && t.category !== 'Pagamento de Fatura').reduce((acc, t) => acc + (t.amount || 0), 0);
      return { name, shortName: name.substring(0, 3), Entradas: income, Saídas: cashExpense, Cartão: cardExpense };
    });
  }, [transactions, selectedYear, cards]);

  const maskValue = (val: number) => showValues ? formatCurrency(val) : "R$ ••••";

  return (
    <div className="pb-24 max-w-full mx-auto">
      <div className="sticky top-0 z-40 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl pt-[max(1rem,env(safe-area-inset-top))] pb-4 space-y-3 -mx-4 px-4 border-b border-slate-200/50 dark:border-slate-800/50 md:relative md:border-none md:p-0 md:mb-6 md:bg-transparent">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-black text-slate-900 dark:text-slate-50 heading-display uppercase tracking-tight">Visão Geral</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
              <p className="text-slate-500 dark:text-slate-400 text-[9px] md:text-xs uppercase font-black tracking-[0.15em]">{fullMonths[selectedMonth]} {selectedYear}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={togglePrivacy}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 w-9 h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center shadow-premium active:scale-95 transition-all hover:border-blue-500/50 group"
              title={showValues ? "Ocultar Valores" : "Mostrar Valores"}
            >
              <i className={`fa-solid ${showValues ? 'fa-eye' : 'fa-eye-slash'} text-xs md:text-base lg:text-lg transition-transform group-hover:scale-110`}></i>
            </button>
            <button onClick={handleAddClick} className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center shadow-xl shadow-blue-500/20 active:scale-95 transition-all group">
              <i className="fa-solid fa-plus text-sm md:text-lg lg:text-xl transition-transform group-hover:rotate-90"></i>
            </button>
          </div>
        </header>

        <div className="flex gap-2">
          <div className="relative flex-1 md:max-w-[180px] group">
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-2.5 md:p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
              {fullMonths.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
          </div>
          <div className="relative w-24 md:w-26 group">
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-2.5 md:p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <i className="fa-solid fa-calendar absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 lg:gap-4">
          <div className="bg-white dark:bg-slate-900 p-3 md:p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium bento-card group hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <div className="w-5 h-5 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center text-[9px] group-hover:scale-110 transition-transform"><i className="fa-solid fa-wallet"></i></div>
              <span className="text-slate-400 font-black uppercase text-[8px] tracking-widest">Saldo Líquido</span>
            </div>
            <p className={`text-sm md:text-base lg:text-xl font-black truncate heading-display transition-colors ${stats.balance >= 0 ? 'text-slate-900 dark:text-slate-100 group-hover:text-blue-600' : 'text-red-500'}`}>{maskValue(stats.balance)}</p>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-widest">Real:</span>
              <span className={`text-[7px] font-black uppercase tracking-widest ${stats.balance - stats.totalCardDebt >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {maskValue(stats.balance - stats.totalCardDebt)}
              </span>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 md:p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium bento-card group hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <div className="w-5 h-5 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center text-[9px] group-hover:scale-110 transition-transform"><i className="fa-solid fa-arrow-up"></i></div>
              <span className="text-slate-400 font-black uppercase text-[8px] tracking-widest">Entradas</span>
            </div>
            <p className="text-sm md:text-base lg:text-xl font-black truncate text-emerald-600 heading-display group-hover:text-emerald-500 transition-colors">{maskValue(stats.income)}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 md:p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium bento-card group hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <div className="w-5 h-5 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center text-[9px] group-hover:scale-110 transition-transform"><i className="fa-solid fa-arrow-down"></i></div>
              <span className="text-slate-400 font-black uppercase text-[8px] tracking-widest">Saídas</span>
            </div>
            <p className="text-sm md:text-base lg:text-xl font-black truncate text-red-600 heading-display group-hover:text-red-500 transition-colors">{maskValue(stats.expense)}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 md:p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium bento-card group hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <div className="w-5 h-5 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center text-[9px] group-hover:scale-110 transition-transform"><i className="fa-solid fa-credit-card"></i></div>
              <span className="text-slate-400 font-black uppercase text-[8px] tracking-widest">Fatura do Mês</span>
            </div>
            <p className={`text-sm md:text-base lg:text-xl font-black truncate heading-display transition-colors ${stats.totalCardDebt > 0 ? 'text-indigo-500 group-hover:text-indigo-400' : 'text-slate-400'}`}>{maskValue(stats.totalCardDebt)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-start">
        <div className="lg:col-span-8 space-y-3 md:space-y-4">
          {stats.subscriptionWarning && (
            <div className={`p-4 rounded-2xl border flex items-center justify-between animate-in slide-in-from-top-2 duration-500 ${
              stats.subscriptionWarning.type === 'danger' 
                ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-600 dark:text-red-400' 
                : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-600 dark:text-amber-400'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stats.subscriptionWarning.type === 'danger' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>
                  <i className={`fa-solid ${stats.subscriptionWarning.type === 'danger' ? 'fa-circle-exclamation' : 'fa-clock'} text-xs`}></i>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">{stats.subscriptionWarning.message}</span>
              </div>
              <button className="px-4 py-2 bg-white dark:bg-slate-900 rounded-lg text-[9px] font-black uppercase tracking-widest shadow-sm hover:scale-105 transition-transform">
                Renovar
              </button>
            </div>
          )}

          {stats.riskAlert && (
            <div className={`p-6 rounded-3xl border relative overflow-hidden animate-in slide-in-from-top-4 duration-700 shadow-xl ${
              stats.riskAlert.type === 'danger' 
                ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900 text-red-700 dark:text-red-400' 
                : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-400'
            }`}>
              <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl -mr-12 -mt-12 opacity-30 ${
                stats.riskAlert.type === 'danger' ? 'bg-red-500' : 'bg-amber-500'
              }`}></div>
              
              <div className="flex items-center gap-4 relative z-10">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-xl ${
                  stats.riskAlert.type === 'danger' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                }`}>
                  <i className={`fa-solid ${stats.riskAlert.icon} text-xl`}></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-[9px] font-black uppercase tracking-[0.2em] mb-1 flex items-center gap-2">
                    {stats.riskAlert.type === 'danger' ? 'ALERTA CRÍTICO' : 'Aviso de Segurança'}
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                  </h4>
                  <p className="text-base font-black leading-tight tracking-tight uppercase mb-0.5 heading-display">{stats.riskAlert.title}</p>
                  <p className="text-[10px] font-bold opacity-80 leading-relaxed">{stats.riskAlert.message}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 p-4 md:p-6 lg:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium h-[280px] md:h-[320px] lg:h-[350px] bento-card">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h3 className="font-black text-slate-800 dark:text-slate-200 uppercase text-[9px] md:text-[10px] tracking-[0.2em] flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                Fluxo Anual
              </h3>
              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  <span className="text-[7px] md:text-[8px] font-black uppercase text-slate-400">Entradas</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                  <span className="text-[7px] md:text-[8px] font-black uppercase text-slate-400">Dinheiro</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  <span className="text-[7px] md:text-[8px] font-black uppercase text-slate-400">Cartão</span>
                </div>
              </div>
            </div>
            <div className="h-full pb-10">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -35, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#94a3b8" opacity={0.05} />
                  <XAxis dataKey="shortName" axisLine={false} tickLine={false} tick={{ fontSize: 8, fontWeight: 900, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8, fontWeight: 700, fill: '#94a3b8' }} />
                  <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.05)', radius: 6 }} content={<CustomTooltip showValues={showValues} />} />
                  <Bar name="Entradas" dataKey="Entradas" fill="#10b981" radius={[3, 3, 0, 0]} barSize={6} />
                  <Bar name="Saídas" dataKey="Saídas" fill="#ef4444" radius={[3, 3, 0, 0]} barSize={6} />
                  <Bar name="Cartão" dataKey="Cartão" fill="#f59e0b" radius={[3, 3, 0, 0]} barSize={6} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-3 md:space-y-4">
          <div className="bg-white dark:bg-slate-900 p-3 md:p-4 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium relative overflow-hidden group bento-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl -mr-16 -mt-16 transition-transform group-hover:scale-110 duration-700"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <p className="text-slate-400 dark:text-slate-500 text-[8px] md:text-[9px] font-black uppercase tracking-[0.15em]">Uso Total de Crédito</p>
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center text-[10px] shadow-sm">
                  <i className="fa-solid fa-credit-card"></i>
                </div>
              </div>
              
              <div className="flex items-baseline gap-2 mb-2 md:mb-3">
                <h2 className="text-xl md:text-2xl font-black tracking-tighter text-slate-900 dark:text-white heading-display">{stats.creditUsagePercent.toFixed(1)}%</h2>
                <span className={`text-[8px] md:text-[9px] font-black uppercase tracking-widest ${
                  stats.creditUsagePercent > 80 ? 'text-red-500' : 
                  stats.creditUsagePercent > 50 ? 'text-amber-500' : 
                  'text-emerald-500'
                }`}>
                  {stats.creditUsagePercent > 80 ? 'Crítico' : stats.creditUsagePercent > 50 ? 'Moderado' : 'Saudável'}
                </span>
              </div>

              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 md:h-2 rounded-full mb-3 md:mb-4 overflow-hidden p-0.5">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_12px_rgba(59,130,246,0.2)] ${
                    stats.creditUsagePercent > 80 ? 'bg-red-500' : 
                    stats.creditUsagePercent > 50 ? 'bg-amber-500' : 
                    'bg-blue-600'
                  }`} 
                  style={{ width: `${Math.min(100, stats.creditUsagePercent)}%` }}
                ></div>
              </div>

              <div className="flex items-center justify-between p-2 md:p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 transition-colors group-hover:border-blue-500/20">
                <div className="flex flex-col">
                  <span className="text-[7px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Livre Global</span>
                  <span className="text-[9px] md:text-[10px] font-black text-slate-900 dark:text-white">{maskValue(stats.availableCredit)}</span>
                </div>
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center shadow-sm">
                  <i className="fa-solid fa-shield-halved text-blue-500/40 text-[8px] md:text-[9px]"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 p-4 md:p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium bento-card">
            <h3 className="font-black text-slate-800 dark:text-slate-200 uppercase text-[9px] md:text-[10px] tracking-[0.2em] mb-4 md:mb-6">Top Categorias</h3>
            <div className="space-y-3 md:space-y-5">
              {stats.categorySummary.map(([cat, amount]) => (
                <div key={cat} className="group cursor-pointer">
                  <div className="flex justify-between items-center text-[9px] md:text-[10px] font-black mb-1.5 md:mb-2">
                    <span className="text-slate-500 dark:text-slate-400 uppercase tracking-widest group-hover:text-blue-500 transition-colors">{cat}</span>
                    <span className="text-slate-900 dark:text-slate-100">{maskValue(amount)}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 md:h-2.5 rounded-full overflow-hidden p-0.5">
                    <div className={`${CATEGORY_CONFIG[cat as CategoryType]?.color || 'bg-slate-400'} h-full rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(0,0,0,0.05)]`} style={{ width: `${Math.min(100, (amount / (stats.expense || 1)) * 100)}%` }}></div>
                  </div>
                </div>
              ))}
              {stats.categorySummary.length === 0 && (
                <div className="py-6 text-center">
                  <i className="fa-solid fa-chart-pie text-slate-200 dark:text-slate-800 text-2xl mb-2"></i>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sem dados no período</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <TransactionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={onAddTransaction} cards={cards} transactions={transactions} />
    </div>
  );
};

export default Dashboard;